import { FC, useContext, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { PatrolAbsorber } from "../../../../../http/patrol/types/absorbers";
import { useAbsorbersApi } from "../../../../../http/patrol/useAbsorbers";
import { PaginationQuery } from "../../../../../types";
import SvgDelete from "../../../../icons/Delete";
import FlexContent from "../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../ui/InlineOrderEditor";
import { getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import AbsorberForm from "./AbsorberForm";
import CreateAbsorberModalDialogButton from "./CreateAbsorberModalDialogButton";
import SystemAbsorberForm from "./SystemAbsorberForm";

type Props = {
  // Header actions (search + create) are only published to the shared Header while
  // this tab is the active one, since Tabs keeps every tab's content mounted at once.
  active?: boolean;
};

const Absorbers: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const { patrolSystemId } = useParams<{ patrolSystemId: string }>();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "absorbers" });
  const aborter = useRef(new AbortController());
  const absorbersApi = useAbsorbersApi();
  const { setActions, setTitle } = useSearchContext();

  const updateVisibility = async (id: string, visibility: VisibilityScopeValue) => {
    try {
      await absorbersApi.updateAdminAbsorberVisibility(id, { visibility });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (absorberId: string, newOrder: number) => {
    try {
      // assume API supports partial update for order
      await absorbersApi.updateAdminAbsorberOrder(absorberId, { order: newOrder });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const [itemToEdit, setItemToEdit] = useState<PatrolAbsorber>();
  const [itemToDelete, setItemToDelete] = useState<PatrolAbsorber>();
  const { accessesAction, accessesDialog } = useEntityAccesses<{ id: string; name?: string }>("patrol.absorber");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "absorbers");

  const isActiveTab = active ?? true;

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = absorbersApi.useAdminSystemAbsorbers(query, aborter.current.signal, patrolSystemId);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.absorbers.plural);
    setActions(
      <>
        <SearchBar placeholder={i18n.translation.absorbers.search} value={searchTerm} onChange={handleSearchChange} />
        <CreateAbsorberModalDialogButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<PatrolAbsorber>
        isTall
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.common.code}</th>
            <th align="left">{i18n.translation.common.description}</th>
            <th align="left">{i18n.translation.materials.singular}</th>
            {!patrolSystemId && <th align="left">{i18n.translation.systemTypes.plural}</th>}
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <td>
              <Link onClick={(e) => e.stopPropagation()} to={`/admin/items/${item.item.id}`}>
                {item.item.code}
              </Link>
            </td>
            <td>{item.item.description}</td>
            <td>{item.material.name}</td>
            {!patrolSystemId && <td>{item.systems.map((x) => x.system.name).join("; ")}</td>}
            <td align="center">
              <Visibility value={item.visibility} onChange={(value) => updateVisibility(item.item.id, value)} />
            </td>
            <td align="center">
              <InlineOrderEditor order={item.order} onUpdate={(newOrder) => updateOrder(item.item.id, newOrder)} />
            </td>
          </>
        )}
        onRowClick={(item) => setItemToEdit(item)}
        rowActions={(item) => [
          getEditAction(i18n, () => setItemToEdit(item)),
          accessesAction({ id: item.item.id, name: item.item.name ?? item.item.code }),
          {
            title: patrolSystemId ? i18n.translation.common.remove : i18n.translation.common.delete,
            onClick: () => setItemToDelete(item),
            icon: SvgDelete,
            isDivider: true,
            color: "red",
          },
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}
      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={patrolSystemId ? i18n.translation.absorbers.removeFromSystem : i18n.translation.absorbers.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                absorbersApi
                  .deleteAdminAbsorber(itemToDelete.item.id, patrolSystemId)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {patrolSystemId
            ? i18n.translation.absorbers.questions.removeFromSystem
            : i18n.translation.absorbers.questions.delete}
        </ModalDialog>
      )}
      {!!itemToEdit && (
        <ModalDialog onClose={() => setItemToEdit(undefined)} title={i18n.translation.absorbers.edit}>
          {patrolSystemId ? (
            <SystemAbsorberForm onClose={() => setItemToEdit(undefined)} defaultValues={itemToEdit} />
          ) : (
            <AbsorberForm onClose={() => setItemToEdit(undefined)} defaultValues={itemToEdit} />
          )}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

export default Absorbers;
